import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchDecisionConfig,
  previewDecisionConfig,
  saveDecisionGlobalConfig,
  saveDecisionUserConfig,
  getDecisionApiError
} from '../../services/decisionApi';
import { ActionBadge, ScoreRing } from './components/DecisionWidgets';
import MarketIndexManualPanel from './components/MarketIndexManualPanel';

const WEIGHT_KEYS = [
  ['intrinsic', '产品内生'],
  ['macro', '宏观匹配'],
  ['personal', '持仓视角'],
  ['ops', '操作约束']
];

function cloneConfig(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export default function ConfigPage({ user }) {
  const [draft, setDraft] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [previewProductId, setPreviewProductId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const isSuper = user?.portalRole === 'superadmin';
  const weightSum = useMemo(() => {
    if (!draft?.weights) return 0;
    return Math.round(
      Object.values(draft.weights).reduce((a, b) => a + Number(b || 0), 0) * 100
    );
  }, [draft]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchDecisionConfig();
        if (cancelled) return;
        setDraft(cloneConfig(data.effective));
        setDefaults(cloneConfig(data.defaults));
        setPreviewProductId(data.effective?.previewSampleProductId || '');
      } catch (err) {
        if (!cancelled) setError(getDecisionApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function updateWeight(key, value) {
    setDraft((prev) => ({
      ...prev,
      weights: { ...prev.weights, [key]: Number(value) }
    }));
  }

  function updateThreshold(key, value) {
    setDraft((prev) => ({
      ...prev,
      thresholds: { ...prev.thresholds, [key]: Number(value) }
    }));
  }

  async function saveGlobal() {
    if (!isSuper) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await saveDecisionGlobalConfig(draft, '管理台可视化保存');
      setMessage('全局策略已保存');
    } catch (err) {
      setError(getDecisionApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function saveUser() {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await saveDecisionUserConfig({ weights: draft.weights, thresholds: draft.thresholds }, '个人偏好');
      setMessage('个人偏好已保存');
    } catch (err) {
      setError(getDecisionApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function runPreview() {
    if (!previewProductId) {
      setError('请填写预览产品 ID');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const data = await previewDecisionConfig(previewProductId, draft);
      setPreview(data.result);
    } catch (err) {
      setError(getDecisionApiError(err));
    } finally {
      setSaving(false);
    }
  }

  function resetDefaults() {
    if (defaults) setDraft(cloneConfig(defaults));
  }

  if (loading || !draft) {
    return <p className="decision-muted">加载配置…</p>;
  }

  return (
    <div>
      <h1 className="decision-page-title">策略配置</h1>
      <p className="decision-page-sub">权重、阈值、信号参数均可视化调整，支持实时预览</p>

      <div className="decision-grid decision-grid-2">
        <div className="decision-card">
          <h3>维度权重（合计 {weightSum}%）</h3>
          {WEIGHT_KEYS.map(([key, label]) => (
            <div className="weight-slider-row" key={key}>
              <span>{label}</span>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round((draft.weights[key] || 0) * 100)}
                onChange={(e) => updateWeight(key, Number(e.target.value) / 100)}
              />
              <span>{Math.round((draft.weights[key] || 0) * 100)}%</span>
            </div>
          ))}
        </div>

        <div className="decision-card">
          <h3>行动阈值</h3>
          <div className="threshold-row">
            <label>
              <div className="decision-muted">Hold ≥</div>
              <input
                className="decision-input"
                type="number"
                value={draft.thresholds.hold}
                onChange={(e) => updateThreshold('hold', e.target.value)}
              />
            </label>
            <label>
              <div className="decision-muted">Reduce ≥</div>
              <input
                className="decision-input"
                type="number"
                value={draft.thresholds.reduce}
                onChange={(e) => updateThreshold('reduce', e.target.value)}
              />
            </label>
            <label>
              <div className="decision-muted">Sell &lt;</div>
              <input
                className="decision-input"
                type="number"
                value={draft.thresholds.sell}
                onChange={(e) => updateThreshold('sell', e.target.value)}
              />
            </label>
          </div>

          <h3 style={{ marginTop: 18 }}>预览产品 ID</h3>
          <input
            className="decision-input"
            value={previewProductId}
            onChange={(e) => setPreviewProductId(e.target.value)}
            placeholder="例如 080"
          />
          <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            <button type="button" className="decision-btn" onClick={runPreview} disabled={saving}>
              实时预览
            </button>
            <button type="button" className="decision-btn ghost" onClick={resetDefaults}>
              恢复默认
            </button>
            <button type="button" className="decision-btn ghost" onClick={saveUser} disabled={saving}>
              保存个人偏好
            </button>
            {isSuper ? (
              <button type="button" className="decision-btn" onClick={saveGlobal} disabled={saving}>
                保存全局策略
              </button>
            ) : null}
          </div>
          {message ? <p className="decision-muted" style={{ marginTop: 10 }}>{message}</p> : null}
          {error ? <p className="decision-error">{error}</p> : null}
        </div>
      </div>

      {preview ? (
        <div className="decision-card" style={{ marginTop: 16 }}>
          <h3>预览结果 · {preview.product_name}</h3>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <ScoreRing score={preview.score} />
            <div>
              <ActionBadge action={preview.action} label={preview.label} />
              <p style={{ marginTop: 8 }}>{preview.summary}</p>
            </div>
          </div>
        </div>
      ) : null}

      {isSuper ? (
        <div style={{ marginTop: 16 }}>
          <MarketIndexManualPanel />
        </div>
      ) : null}
    </div>
  );
}
